import React, { useEffect, useState, useCallback } from 'react'
import { Box, Text, Button, Stack, Loader, TextInput, Group, Badge, Paper } from '@mantine/core'
import { FiSearch, FiX, FiExternalLink } from 'react-icons/fi'
import { observer } from 'mobx-react-lite'
import agent from '../app/api/agent'
import { useStore } from '../app/stores/store'
import { extractThumbnailFromVideo } from './ThumbnailExtractor'
import type { FirearmModel } from '../app/models/firearmModel'

const ShootsDetailsScreen = observer(function ShootsDetailsScreen () {
  const { editorStore, uploadDrawerStore } = useStore()
  const [firearmModels, setFirearmModels] = useState<FirearmModel[]>([])
  const [cartridgeOptions, setCartridgeOptions] = useState<{ value: string; label: string }[]>([])
  const [listsLoading, setListsLoading] = useState(true)
  const [searchTerm, setSearchTerm] = useState('')

  const selectedModel = firearmModels.find(m => m.id === editorStore.linkedFirearmModelId)
  const filteredModels = firearmModels.filter(m =>
    `${m.make} ${m.model} ${m.caliber}`.toLowerCase().includes(searchTerm.toLowerCase())
  ).slice(0, 8)

  useEffect(() => {
    let cancelled = false
    setListsLoading(true)
    void (async () => {
      try {
        const [fm, cs] = await Promise.all([
          agent.FirearmModels.list({ pageSize: 100 }),
          agent.CartridgeSpecs.list({ pageSize: 80 })
        ])
        if (cancelled) return
        setFirearmModels(fm.items)
        setCartridgeOptions(
          cs.items.map((c) => ({
            value: c.id,
            label: (c.shortName || c.name || '').trim() || c.id
          }))
        )
      } catch {
        if (!cancelled) {
          setFirearmModels([])
          setCartridgeOptions([])
        }
      } finally {
        if (!cancelled) setListsLoading(false)
      }
    })()
    return () => {
      cancelled = true
    }
  }, [])

  const handlePost = useCallback(async () => {
    const photoUrl = editorStore.photoUrl
    const mediaType = editorStore.mediaType
    if (!photoUrl || !mediaType) return

    let thumbnailBlob: Blob | undefined
    if (mediaType === 'video') {
      try {
        thumbnailBlob = await extractThumbnailFromVideo(photoUrl)
      } catch {
        /* optional thumbnail */
      }
    }
    await editorStore.postShoot(() => void uploadDrawerStore.afterSuccessfulShootFromEditor(), thumbnailBlob)
  }, [editorStore, uploadDrawerStore])

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        width: '100%',
        maxWidth: '100%',
        backgroundColor: 'var(--gs-bg-2)',
        borderRadius: 8,
        overflow: 'hidden',
        border: '1px solid var(--gs-border)'
      }}
    >
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          padding: '10px 12px',
          borderBottom: '1px solid var(--gs-border)',
          flexShrink: 0
        }}
      >
        <Button variant="subtle" size="sm" compact onClick={() => uploadDrawerStore.backToEditor()} sx={{ fontWeight: 600 }}>
          &lt; Back
        </Button>
        <Button
          variant="filled"
          color="orange"
          size="sm"
          radius="xl"
          loading={editorStore.isPosting}
          disabled={editorStore.isPosting || !editorStore.canPost}
          onClick={() => void handlePost()}
          px="lg"
        >
          Post
        </Button>
      </Box>

      <Box sx={{ padding: 12, flex: 1, minHeight: 0, overflow: 'auto' }}>
        {listsLoading
          ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 'xl' }}>
            <Loader size="sm" />
          </Box>
            )
          : (
          <Stack spacing="md">
            {/* Firearm Model Section */}
            <Box>
              <Text size="sm" fw={600} mb={8}>Link a firearm model (optional)</Text>
              <Text size="xs" c="dimmed" mb={12}>
                Connect this video to a firearm from the registry to help others find related content
              </Text>

              <TextInput
                placeholder="Search firearm models..."
                icon={<FiSearch size={16} />}
                size="sm"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.currentTarget.value)}
                mb={selectedModel ? 12 : 8}
              />

              {/* Selected Model Display */}
              {selectedModel && (
                <Paper p="sm" mb={12} sx={{ backgroundColor: 'var(--gs-surface2)', border: '1px solid var(--gs-border)', borderRadius: 6 }}>
                  <Group position="apart">
                    <Box>
                      <Text size="sm" fw={600}>{selectedModel.make} {selectedModel.model}</Text>
                      <Group spacing={6} mt={4}>
                        {selectedModel.caliber && (
                          <Badge size="xs" variant="light">{selectedModel.caliber}</Badge>
                        )}
                        {selectedModel.platform && (
                          <Badge size="xs" variant="light">{selectedModel.platform}</Badge>
                        )}
                      </Group>
                    </Box>
                    <Button
                      variant="subtle"
                      size="xs"
                      color="gray"
                      onClick={() => editorStore.setLinkedFirearmModelId(null)}
                    >
                      <FiX size={16} />
                    </Button>
                  </Group>
                </Paper>
              )}

              {/* Model Search Results */}
              {searchTerm && (
                <Stack spacing={6} mb={8}>
                  {filteredModels.length > 0
                    ? filteredModels.map(model => (
                      <Paper
                        key={model.id}
                        p={8}
                        sx={{
                          backgroundColor: model.id === editorStore.linkedFirearmModelId ? 'var(--gs-surface3)' : 'var(--gs-surface2)',
                          border: `1px solid ${model.id === editorStore.linkedFirearmModelId ? '#E85D04' : 'var(--gs-border)'}`,
                          borderRadius: 6,
                          cursor: 'pointer',
                          transition: 'all 0.15s',
                          '&:hover': {
                            borderColor: '#E85D04',
                            backgroundColor: 'var(--gs-surface3)'
                          }
                        }}
                        onClick={() => editorStore.setLinkedFirearmModelId(model.id)}
                      >
                        <Text size="sm" fw={600}>{model.make} {model.model}</Text>
                        <Group spacing={6} mt={4}>
                          {model.caliber && <Badge size="xs" variant="light">{model.caliber}</Badge>}
                          {model.platform && <Badge size="xs" variant="light">{model.platform}</Badge>}
                        </Group>
                      </Paper>
                    ))
                    : (
                      <Text size="xs" c="dimmed">No models found. Browse the registry to discover more.</Text>
                    )}
                </Stack>
              )}

              <Group spacing={8} mt={8}>
                <Button
                  variant="default"
                  size="xs"
                  rightIcon={<FiExternalLink size={14} />}
                  component="a"
                  href="/registry"
                  target="_blank"
                >
                  Browse Full Registry
                </Button>
              </Group>
            </Box>

            {/* Cartridge Section */}
            <Box>
              <Text size="sm" fw={600} mb={8}>Cartridge/Ammunition (optional)</Text>
              <Text size="xs" c="dimmed" mb={8}>
                Specify what you were shooting to help viewers understand the context
              </Text>
              {/* Cartridge select */}
              <Box
                component="select"
                value={editorStore.linkedCartridgeSpecId || ''}
                onChange={(e) => editorStore.setLinkedCartridgeSpecId(e.currentTarget.value || null)}
                sx={{
                  width: '100%',
                  padding: '8px 12px',
                  fontSize: 14,
                  borderRadius: 4,
                  border: '1px solid var(--gs-border)',
                  backgroundColor: 'var(--gs-surface2)',
                  color: 'var(--gs-text)',
                  cursor: 'pointer',
                  '&:hover': { borderColor: '#E85D04' },
                  '&:focus': { outline: 'none', borderColor: '#E85D04' }
                }}
              >
                <option value="">None</option>
                {cartridgeOptions.map(opt => (
                  <option key={opt.value} value={opt.value}>
                    {opt.label}
                  </option>
                ))}
              </Box>
            </Box>
          </Stack>
            )}

        {editorStore.error && (
          <Text size="sm" c="red" mt="xs">
            {editorStore.error}
          </Text>
        )}
      </Box>
    </Box>
  )
})

export default ShootsDetailsScreen
